import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { apiService, Appointment, Provider, Procedure, resolveLocalized } from '../../services/apiService';

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending (Website)',
  scheduled: 'Scheduled',
  confirmed: 'Confirmed',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'No Show',
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-purple-100 text-purple-800',
  scheduled: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-green-100 text-green-800',
  completed: 'bg-blue-100 text-blue-800',
  cancelled: 'bg-red-100 text-red-800',
  no_show: 'bg-gray-200 text-gray-700',
};

const AppointmentsPage: React.FC = () => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [providerFilter, setProviderFilter] = useState<string>('');
  const [procedureFilter, setProcedureFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const [a, p, r] = await Promise.all([
          apiService.getAppointments(),
          apiService.getProviders(false),
          apiService.getProcedures(false),
        ]);
        setAppointments(a.data.data || []);
        setProviders(p.data.data || []);
        setProcedures(r.data.data || []);
      } catch (e: any) {
        setError(e?.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    return appointments.filter(a => {
      if (search && !`${a.patient_name} ${a.patient_email}`.toLowerCase().includes(search.toLowerCase())) return false;
      if (from && a.appointment_date < from) return false;
      if (to && a.appointment_date > to) return false;
      if (providerFilter && String(a.provider_id) !== providerFilter) return false;
      if (procedureFilter && String(a.procedure_id) !== procedureFilter) return false;
      if (statusFilter && a.status !== statusFilter) return false;
      return true;
    }).sort((x, y) => {
      const ck = `${x.appointment_date} ${x.start_time}`.localeCompare(`${y.appointment_date} ${y.start_time}`);
      return ck;
    });
  }, [appointments, search, from, to, providerFilter, procedureFilter, statusFilter]);

  const resetFilters = () => {
    setSearch(''); setFrom(''); setTo(''); setProviderFilter(''); setProcedureFilter(''); setStatusFilter('');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Appointments</h2>
        <span className="text-sm text-gray-500">{filtered.length} of {appointments.length}</span>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded">{error}</div>}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="lg:col-span-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">Search (name or email)</label>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="e.g. Müller or @example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-navy-admin" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded">
              <option value="">All</option>
              {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Provider</label>
            <select value={providerFilter} onChange={e => setProviderFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded">
              <option value="">All</option>
              {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Procedure</label>
            <select value={procedureFilter} onChange={e => setProcedureFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded">
              <option value="">All</option>
              {procedures.map(p => <option key={p.id} value={p.id}>{resolveLocalized(p.name)}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <button onClick={resetFilters} className="text-sm text-blue-600 hover:underline">Reset filters</button>
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No appointments match your filters.</div>
        ) : (
          <>
            {/* Desktop: table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date / Time</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Procedure</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Room</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filtered.map(a => (
                    <tr key={a.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{a.appointment_date}</div>
                        <div className="text-xs text-gray-500">{a.start_time}–{a.end_time}</div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-900">{a.patient_name}</div>
                        <div className="text-xs text-gray-500">{a.patient_email}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{a.provider_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{a.procedure_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{a.operatory_name}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 text-xs rounded-full ${STATUS_COLORS[a.status] || ''}`}>
                          {STATUS_LABELS[a.status] || a.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-sm">
                        <Link to={`/admin/calendar?date=${a.appointment_date}&id=${a.id}`}
                          className="text-blue-600 hover:underline whitespace-nowrap">Open in calendar →</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: card list */}
            <ul className="md:hidden divide-y divide-gray-200">
              {filtered.map(a => (
                <li key={a.id}>
                  <Link to={`/admin/calendar?date=${a.appointment_date}&id=${a.id}`}
                    className="block p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{a.patient_name}</div>
                        <div className="text-xs text-gray-500">{a.appointment_date} · {a.start_time}–{a.end_time}</div>
                        <div className="text-xs text-gray-500 mt-1">{a.procedure_name} · {a.provider_name}</div>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${STATUS_COLORS[a.status] || ''}`}>
                        {STATUS_LABELS[a.status] || a.status}
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

export default AppointmentsPage;
